// Agent API Service
import type {
  AgentChatRequest,
  AgentChatResponse,
  ConversationsResponse,
  ConversationDetailResponse
} from '../types/agent';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Generic fetch wrapper with error handling (same as api.ts)
async function fetchAPI<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;

  const config: RequestInit = {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(error.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Agent API Error:', error);
    throw error;
  }
}

/**
 * Send a message to the agent
 */
export async function sendMessage(request: AgentChatRequest): Promise<AgentChatResponse> {
  return fetchAPI<AgentChatResponse>('/api/agent/chat', {
    method: 'POST',
    body: JSON.stringify(request)
  });
}

/**
 * Get user's conversation history
 */
export async function getConversations(userId: string): Promise<ConversationsResponse> {
  return fetchAPI<ConversationsResponse>(`/api/agent/conversations?userId=${encodeURIComponent(userId)}`, {
    method: 'GET'
  });
}

/**
 * Get full conversation with messages
 */
export async function getConversation(conversationId: string, userId: string): Promise<ConversationDetailResponse> {
  return fetchAPI<ConversationDetailResponse>(`/api/agent/conversations/${conversationId}?userId=${encodeURIComponent(userId)}`, {
    method: 'GET'
  });
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string, userId: string): Promise<{ success: boolean }> {
  return fetchAPI<{ success: boolean }>(`/api/agent/conversations/${conversationId}?userId=${encodeURIComponent(userId)}`, {
    method: 'DELETE'
  });
}
